import { CloseIcon } from '@/assets/icons/button';
import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';

interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
  contentLabel: string | ReactNode;
  contentSubtitle?: string;
  children: ReactNode;
  /** Optional content to render pinned at the bottom of the modal. */
  footer?: ReactNode;
  className?: string;
  closeOnClickOff?: boolean;
}

// Using defaultProps for functional components will be deprecated soon. Write default values inline if needed.
function ModalComponent({
  isOpen,
  onRequestClose,
  contentLabel,
  contentSubtitle,
  children,
  className,
  closeOnClickOff,
  footer,
}: Props): React.JSX.Element | null {
  // Detect if consumer passed an explicit height / max-height utility so we do not override it
  const userHasHeightClass = className?.match(/(?:^|\s)(h-|max-h-)/);

  // Constrain overall modal height to the viewport (90vh) and allow vertical scrolling when content overflows.
  // This keeps existing behaviour for large desktop resolutions while gracefully degrading on smaller screens.
  const defaultStyle = `flex flex-col relative bg-surface_gray_50 border border-border_gray_200 rounded-2xl shadow-lg z-10 lg:max-w-[1112px] max-w-screen-md m-4 ${
    userHasHeightClass ? '' : 'max-h-[90vh]'
  }`;

  const onClickOff = (): void => {
    if (closeOnClickOff === false) {
      return;
    }
    onRequestClose();
  };

  const modalJsx = (
    <div
      className={`fixed top-0 left-0 w-full h-full flex items-center justify-center z-[1000] ${isOpen ? '' : 'hidden'}`}
    >
      <div
        className='fixed top-0 left-0 w-full h-full bg-background_overlay'
        onClick={onClickOff}
      ></div>
      <div className={`${defaultStyle} ${className}`}>
        <div className='flex h-[52px] py-2 px-[10px]'>
          <button
            className='ml-auto mr-0 w-[36px] h-[36px] p-[7px] hover:bg-primary_highlight dark:hover:bg-primary_dark rounded-full transition-colors'
            aria-label='Close modal'
            onClick={onRequestClose}
          >
            <CloseIcon className='w-[22px] h-[22px]' />
          </button>
        </div>
        <div className='flex justify-center w-full items-center px-8'>
          <div className='flex flex-col gap-0 w-full'>
            {typeof contentLabel === 'string' && contentLabel !== '' ? (
              <div className='h-8 w-full flex items-center'>
                <h2
                  className={`m-0 p-0 max-h-full text-center font-semibold text-2xl leading-8 tracking-[0%]`}
                >
                  {contentLabel}
                </h2>
              </div>
            ) : (
              <div className='w-full flex justify-center'>{contentLabel}</div>
            )}
            {contentSubtitle && contentSubtitle !== '' && (
              <div className='h-6 w-full flex items-center'>
                <p className='font-normal text-base leading-6 tracking-[0%]'>{contentSubtitle}</p>
              </div>
            )}
          </div>
        </div>
        <div className='h-6 w-full flex items-center px-[21px]'>
          {(contentLabel !== '' || contentSubtitle) && (
            <div className='flex flex-col h-full w-full gap-2.5 py-4'>
              <hr className='border-t-2 border-gray-300' />
            </div>
          )}
        </div>
        {/* Scrollable body */}
        <div className='flex flex-col px-8 pb-4 flex-1 overflow-y-auto'>{children}</div>

        {/* Optional footer */}
        {footer && <div className='w-full px-8 pb-4'>{footer}</div>}
      </div>
    </div>
  );

  return typeof window !== 'undefined' ? ReactDOM.createPortal(modalJsx, document.body) : null;
}

export default ModalComponent;
